import Box from '@mui/material/Box';

import { highlight, neutralLight } from 'src/theme/palette';

export interface LoaderProps {
  /** 'spinner' for infinite animation, 'progress' for determinate arc */
  variant?: 'spinner' | 'progress';
  /** 0–100, only relevant when variant='progress' */
  value?: number;
  /** Diameter in px (default 32) */
  size?: number;
  color?: string;
  ariaLabel?: string;
  ariaValueText?: string;
  ariaHidden?: boolean;
  strokeWidth?: number;
}

const STROKE_WIDTH = 3;

export function Loader({
  variant = 'spinner',
  value = 0,
  size = 32,
  color = highlight.darkest,
  ariaLabel,
  ariaValueText,
  ariaHidden = false,
  strokeWidth = STROKE_WIDTH,
}: LoaderProps) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.min(100, Math.max(0, value));
  const readableValueText = ariaValueText ?? `${Math.round(clamped)} percent complete`;
  const offset = circumference - (clamped / 100) * circumference;
  const center = size / 2;

  return (
    <Box
      aria-hidden={ariaHidden}
      role={variant === 'progress' ? 'progressbar' : 'status'}
      aria-valuenow={variant === 'progress' ? clamped : undefined}
      aria-valuemin={variant === 'progress' ? 0 : undefined}
      aria-valuemax={variant === 'progress' ? 100 : undefined}
      aria-valuetext={variant === 'progress' ? readableValueText : undefined}
      aria-label={ariaLabel ?? (variant === 'spinner' ? 'Loading' : undefined)}
      sx={{
        display: 'inline-flex',
        width: size,
        height: size,
        flexShrink: 0,
        ...(variant === 'spinner' && {
          '@keyframes lta-spin': {
            from: { transform: 'rotate(0deg)' },
            to: { transform: 'rotate(360deg)' },
          },
          animation: 'lta-spin 1.2s linear infinite',
        }),
      }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none">
        {/* Track */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          stroke={neutralLight.dark}
          strokeWidth={strokeWidth}
        />
        {/* Arc */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={variant === 'progress' ? offset : circumference * 0.75}
          transform={`rotate(-90 ${center} ${center})`}
        />
      </svg>
    </Box>
  );
}
